import { useEffect, useRef } from "react";
import MuiDialog from "@mui/material/Dialog";

/**
 * MUI's dialog, with the focus handed back at a moment that is safe to hand it
 * back at.
 *
 * ## The bug
 *
 * While a modal is open, MUI marks the rest of the page `aria-hidden` so a
 * screen reader cannot wander out of it. On close it does two things: it
 * removes that attribute, and it returns focus to whatever opened the dialog.
 *
 * The second happens first. For one commit the browser has focus sitting on an
 * element inside a subtree that is still hidden from assistive technology, and
 * Chrome says so:
 *
 *     Blocked aria-hidden on an element because its descendant retained focus.
 *
 * For a screen reader user that is a moment where focus is somewhere they have
 * been told does not exist. It is brief, and "brief" is not a property anybody
 * navigating by keyboard can rely on.
 *
 * ## Why it is fixed here and not at the call sites
 *
 * Every dialog in this product had it, because every dialog is this dialog.
 * Six call sites across two apps, one cause. Fixing the one somebody happened
 * to be looking at would have left five, and the next new dialog would have
 * had it again — which is the failure this package exists to prevent.
 *
 * ## Why not `closeAfterTransition`, and why not `inert`
 *
 * `closeAfterTransition` makes it worse rather than better. Read
 * `Modal/useModal.js`: with it unset the attribute is removed as soon as
 * `open` goes false, and with it set removal waits for the exit transition to
 * finish — so the window this is about gets *longer*, for the length of an
 * animation.
 *
 * `inert` is what Chrome's message suggests and it is the right long-term
 * answer, because an inert subtree cannot hold focus at all. MUI 9.0.1 still
 * writes `aria-hidden` (`Modal/ModalManager.js`), so there is no prop to turn
 * that on and this is the version-appropriate fix rather than the ideal one.
 *
 * ## What it does
 *
 * Remembers what had focus when the dialog opened, tells MUI not to restore it
 * — `disableRestoreFocus` — and puts it back once no ancestor of that element
 * is `aria-hidden` any more. The condition, watched, rather than a guess at
 * how many frames MUI's cleanup takes: the first attempt guessed one frame,
 * guessed wrong, and reproduced the same warning from the other side.
 *
 * **If the opener is gone by then, focus is not forced anywhere.** A dialog
 * that confirmed something usually causes the list behind it to re-read, and
 * the element that opened it may not exist any more; moving focus to a
 * near-miss would be worse than leaving it, because a keyboard user who lands
 * somewhere they did not ask for has to work out where they are. This is the
 * same outcome MUI gives today for a detached opener, made deliberate.
 */
export default function Dialog({ open, children, ...rest }) {
  const opener = useRef(null);

  // Before MUI pulls focus into the dialog, not after.
  useEffect(() => {
    if (open) opener.current = document.activeElement;
  }, [open]);

  useEffect(() => {
    // Also runs on mount, when nothing has opened yet and there is nothing to
    // put back.
    if (open || !opener.current) return undefined;
    const element = opener.current;
    opener.current = null;

    // Wait for the condition, not for a guess at how long it takes.
    //
    // The first version of this waited one animation frame and assumed MUI's
    // cleanup would have run by then. It had not: the warning this whole
    // component exists to remove carried on appearing, now caused by *this
    // code* putting focus back into a subtree that was still hidden. A timing
    // assumption replaced somebody else's timing assumption and the bug did
    // not move.
    //
    // So this watches the attribute itself. Focus goes back when no ancestor
    // of the element is `aria-hidden` any more, which is the thing that
    // actually has to be true, and a MutationObserver says when rather than
    // being asked repeatedly.
    let observer;
    let cancelled = false;

    const restore = () => {
      if (cancelled) return;
      cancelled = true;
      observer?.disconnect();
      if (element.isConnected) element.focus?.();
    };

    const hiddenAncestor = () => element.closest?.('[aria-hidden="true"]');

    if (!hiddenAncestor()) {
      restore();
    } else {
      observer = new MutationObserver(() => {
        if (!hiddenAncestor()) restore();
      });
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["aria-hidden"],
        subtree: true,
      });
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [open]);

  return (
    <MuiDialog open={open} disableRestoreFocus {...rest}>
      {children}
    </MuiDialog>
  );
}
